import { makeAutoObservable, runInAction } from "mobx";
import CoverageService, {
    COVERAGE_FEES_MANAGMENT_OPTIONS,
    COVERAGE_SELECTION_METHOD_OPTIONS,
    ICoverageSettings,
    IShopifyExtensionData,
} from "../../../services/api/coverage-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import { isSet, toBoolean } from "../../../models/model-types-methods";
import { IChangeable, ISaveable } from "../../../stores/types";
import { IIframeService } from "../../../services/iframe-service/iframe-service";
import returnGuardWarningStore from "../../../components/return-guard-warning-component/return-guard-warning-store";

interface ICoverageSettingsChanges extends Partial<ICoverageSettings> {};

class CoverageSettingsStore implements ISaveable, IChangeable<ICoverageSettings> {

    private _model?: ICoverageSettings;
    private _shopifyExtensionData?: IShopifyExtensionData;
    private _changes: ICoverageSettingsChanges = {};
    private _isLoading: boolean = false;
    private _isLoadingShopifyExtensionData: boolean = false;
    private _isSaving: boolean = false;

    private get service(): CoverageService {
        return rootContainer.get<CoverageService>(TYPE.CoverageService);
    }

    constructor() {
        makeAutoObservable(this, {},{autoBind: true}); 
    }

    private get iframeService(): IIframeService {
        return rootContainer.get<IIframeService>(TYPE.IframService);
    }

    get isLoading(): boolean { return this._isLoading; }
    get isLoadingShopifyExtensionData(): boolean { return this._isLoadingShopifyExtensionData; }
    get isLoadedhopifyExtensionData(): boolean { return this._shopifyExtensionData !== undefined; }
    get isSellingCoverage(): boolean { return this._changes.is_selling_coverage ?? this._model?.is_selling_coverage ?? false; }
    get feesManagementSelectedOption(): COVERAGE_FEES_MANAGMENT_OPTIONS| undefined { return this._changes.fees_management_option ?? this._model?.fees_management_option; }
    get coverageSelectionMethodOption(): COVERAGE_SELECTION_METHOD_OPTIONS {
        return this._changes.selection_method_option ?? this._model?.selection_method_option ?? COVERAGE_SELECTION_METHOD_OPTIONS.TOGGLE;
    }
    get isShopifyExtensionEnabled(): boolean { return this._shopifyExtensionData?.is_shopify_extension_enabled ?? false; }
    get shopifyExtensionUrl(): string { return this._shopifyExtensionData?.shopify_extension_url ?? ''; }
    get shouldShowCoverageSettingsPage(): boolean | undefined { return this._model?.show_coverage_settings_page; }
    get isSysAdmin(): boolean { return this._model?.is_sysadmin ?? false; }

    public isSaving(): boolean {
        return this._isSaving;
    }

    private _setModel(model: ICoverageSettings) {
        this.discardChanges();
        this._model = ensureValidModel(model);
    }

    private _setShopifyExtensionData(data: IShopifyExtensionData) {
        this._shopifyExtensionData = data;
    }

    public getUpdatedModel(): ICoverageSettings {
        return {...this._model!,...this._changes};
    }

    public hasChanges(): boolean { 
        return Object.keys(this._changes).length > 0; 
    }

    public discardChanges(): void {
        this._changes = {};
    }

    public async load() {
        this._isLoading = true;
        try{
            const settings = await this.service.fetchCoverageSettings();
            runInAction(() => {
                this._setModel(settings);
                this._isLoading = false;
            });
        }
        catch(e) {
            runInAction(() => this._isLoading = false);
            console.error("Error loading coverage settings: ", e);
        }
    }

    public async loadExtensionData() {
        this._isLoadingShopifyExtensionData = true;
        try{
            const data = await this.service.fetchShopifyExtensionData();
            runInAction(() => {
                this._setShopifyExtensionData(data);
                this._isLoadingShopifyExtensionData = false;
            });
        }
        catch(e) {
            runInAction(() => this._isLoadingShopifyExtensionData = false);
            console.error("Error loading Shopify coverage extension: ", e);
        }
    }

    public async save() {
        this._isSaving = true;
        const shouldUpdateReturnGuardWarning = isSet(this._changes.is_selling_coverage);

        try {
            const settings = this.getUpdatedModel();
            const updatedSettigns = await this.service.saveCoverageSettings(settings);
            runInAction(() => {
                this._isSaving = false;
                this._setModel(updatedSettigns);
                this.iframeService.updateLeftMenuReturnGuard(updatedSettigns.is_selling_coverage);
                /** when is selling coverage was changed - reload the state of the coverage warning */
                if (shouldUpdateReturnGuardWarning) {
                    returnGuardWarningStore.load();
                }
            });
        }
        catch(e){
            runInAction(() => this._isSaving = false);
            console.log('Error trying to save coverage settings: ', e);
        }
    }

    public setIsSellingCoverage(v: boolean) {
        this._changes.is_selling_coverage = v;
        this._clearUnneededChanges();
    }

    public setFeesManagementSelectedOption(v: COVERAGE_FEES_MANAGMENT_OPTIONS|undefined) {
        this._changes.fees_management_option = v;
        this._clearUnneededChanges();
    }

    public setCoverageSelectionMethodOption(v: COVERAGE_SELECTION_METHOD_OPTIONS|undefined) {
        this._changes.selection_method_option = v;
        this._clearUnneededChanges();
    }

    public async onShowCoverageSettingsPageClick() {
        try {
            this.service.enableShowCoverageSettingsPage();
            runInAction(() => {
                this.load();
            });
        
        } catch(e) {
            runInAction(() => 
                console.log('Error trying to enable coverage settings page: ', e)
            );
            
        }
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof ICoverageSettings;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }
}

function ensureValidModel(model: ICoverageSettings) {
    return {...model,
        is_selling_coverage: toBoolean(model.is_selling_coverage),
    }
}

export default new CoverageSettingsStore();